import { useCallback, useEffect, useState } from 'react';
import {
  Users, Search, Plus, RefreshCw, Trash2, CheckCircle, XCircle, KeyRound,
  Building2, Shield, ChevronLeft, ChevronRight, AlertCircle, Mail, Send,
} from 'lucide-react';
import Card from '../../components/Card';
import Button from '../../components/Button';
import Modal from '../../components/Modal';
import LoadingSpinner from '../../components/LoadingSpinner';
import EmptyState from '../../components/EmptyState';
import { useConfirm } from '../../components/ConfirmDialog';
import { adminApi, type AdminUserSummary, type AdminUserDetail, type AdminStats } from '../../api/admin';
import { customerPortalApi, hasUsableEmail } from '../../api/customerPortal';
import { formatDate, timeAgoWithDate } from '../../utils/formatTime';

type DetailTab = 'profile' | 'workspaces' | 'security';

/**
 * Users app — the former "User Admin" page, folded into the Customer Portal so
 * there is ONE user-management system (owner directive 2026-09-24).
 */
export default function UsersApp() {
  const confirm = useConfirm();
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [users, setUsers] = useState<AdminUserSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState<AdminUserDetail | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [detailTab, setDetailTab] = useState<DetailTab>('profile');
  const [showCreate, setShowCreate] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState('');
  const [sendingLinkId, setSendingLinkId] = useState('');
  const perPage = 25;

  const [editName, setEditName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [createEmail, setCreateEmail] = useState('');
  const [createName, setCreateName] = useState('');
  const [createPassword, setCreatePassword] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    const results = await Promise.allSettled([
      adminApi.getStats(),
      adminApi.listUsers({ page, per_page: perPage, search }),
    ]);
    const [s, u] = results;
    const fails: string[] = [];
    if (s.status === 'fulfilled') setStats(s.value); else fails.push('stats');
    if (u.status === 'fulfilled') { setUsers(u.value.users); setTotal(u.value.total); } else fails.push('users');
    setError(fails.length ? `Could not load: ${fails.join(', ')}.` : '');
    setLoading(false);
  }, [page, search]);

  useEffect(() => { void load(); }, [load]);

  const openUser = async (id: string) => {
    try {
      const detail = await adminApi.getUser(id);
      setSelectedUser(detail);
      setEditName(detail.name || '');
      setEditEmail(detail.email);
      setNewPassword('');
      setDetailTab('profile');
      setShowModal(true);
    } catch (e: any) { setError(e.response?.data?.detail || 'Could not load user'); }
  };

  const withSave = async (fn: () => Promise<unknown>) => {
    setSaving(true); setError('');
    try { await fn(); } catch (e: any) { setError(e.response?.data?.detail || 'Action failed'); }
    finally { setSaving(false); }
  };

  const handleSaveName = () => selectedUser && withSave(async () => { await adminApi.updateName(selectedUser.id, editName); await openUser(selectedUser.id); await load(); });
  const handleSaveEmail = () => selectedUser && withSave(async () => { await adminApi.updateEmail(selectedUser.id, editEmail); await openUser(selectedUser.id); await load(); });
  const handleResetPassword = () => selectedUser && withSave(async () => { await adminApi.resetPassword(selectedUser.id, newPassword); setNewPassword(''); });
  const handleToggleStatus = () => selectedUser && withSave(async () => { await adminApi.updateStatus(selectedUser.id, !selectedUser.is_active); await openUser(selectedUser.id); await load(); });

  const handleDelete = async () => {
    if (!selectedUser) return;
    const ok = await confirm({
      title: `Deactivate ${selectedUser.email}?`,
      message: 'The account will be deactivated and lose access.',
      actions: [
        { label: 'Cancel', value: false, tone: 'secondary' },
        { label: 'Deactivate', value: 'deactivate', tone: 'danger' },
      ],
    });
    if (ok === false) return;
    await withSave(async () => { await adminApi.deleteUser(selectedUser.id); setShowModal(false); await load(); });
  };

  const handleCreate = () => withSave(async () => {
    if (!createEmail || !createName || !createPassword) return;
    await adminApi.createUser({ email: createEmail, name: createName, password: createPassword });
    setShowCreate(false); setCreateEmail(''); setCreateName(''); setCreatePassword('');
    await load();
  });

  const handleRoleChange = (workspaceId: string, role: string) => selectedUser && withSave(async () => {
    await adminApi.updateRole(selectedUser.id, workspaceId, role);
    await openUser(selectedUser.id);
  });

  const showToast = (msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(''), 4500);
  };

  const handleSendSupportLink = async (id: string, email: string) => {
    if (!hasUsableEmail(email)) return;
    setSendingLinkId(id); setError('');
    try {
      const res = await customerPortalApi.sendSupportLink(id);
      showToast(`Support link emailed to ${res.emailed_to}`);
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Could not send the support link.');
    } finally { setSendingLinkId(''); }
  };

  if (loading && !stats) return <div className="flex h-40 items-center justify-center"><LoadingSpinner size="lg" label="Loading users…" /></div>;

  return (
    <div className="space-y-5">
      {toast && (
        <div className="fixed bottom-5 right-5 z-50 flex items-center gap-2 rounded-lg border border-green-300 bg-green-50 px-4 py-3 text-sm font-medium text-green-700 shadow-lg dark:border-green-800 dark:bg-green-900/40 dark:text-green-300">
          <Mail size={16} />{toast}
        </div>
      )}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2"><Users size={18} className="text-slate-500" />
          <div><h2 className="text-base font-semibold text-slate-950 dark:text-slate-100">Users</h2>
          <p className="text-sm text-slate-500">Create, edit, and manage every account.</p></div>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="secondary" onClick={() => load()} disabled={loading}><RefreshCw size={14} />Refresh</Button>
          <Button size="sm" onClick={() => setShowCreate(true)}><Plus size={14} />Create User</Button>
        </div>
      </div>

      {error && <div className="flex items-center gap-2 rounded-lg border border-red-300 bg-red-50 px-3 py-2.5 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-400"><AlertCircle size={16} />{error}</div>}

      {stats && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Card className="p-3 text-center"><p className="text-2xl font-bold text-slate-900 dark:text-slate-100">{stats.total_users}</p><p className="text-xs text-slate-500">Total Users</p></Card>
          <Card className="p-3 text-center"><p className="text-2xl font-bold text-green-600 dark:text-green-400">{stats.active_users}</p><p className="text-xs text-slate-500">Active</p></Card>
          <Card className="p-3 text-center"><p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{stats.new_users_30d}</p><p className="text-xs text-slate-500">New (30d)</p></Card>
          <Card className="p-3 text-center"><p className="text-2xl font-bold text-purple-600 dark:text-purple-400">{stats.total_workspaces}</p><p className="text-xs text-slate-500">Workspaces</p></Card>
        </div>
      )}

      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input type="text" value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} placeholder="Search by name or email…"
          className="w-full rounded-lg border border-slate-300 bg-white py-2.5 pl-10 pr-3 text-sm text-slate-950 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100" />
      </div>

      {users.length === 0 ? (
        <EmptyState icon={<Users size={28} />} title="No users found" description="Try a different search or create a new user." />
      ) : (
        <div className="overflow-hidden rounded-lg border border-slate-200 dark:border-slate-700">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 dark:bg-slate-800/50">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-slate-600 dark:text-slate-400">User</th>
                <th className="px-4 py-3 text-left font-medium text-slate-600 dark:text-slate-400">Status</th>
                <th className="px-4 py-3 text-left font-medium text-slate-600 dark:text-slate-400">Role</th>
                <th className="hidden px-4 py-3 text-left font-medium text-slate-600 dark:text-slate-400 md:table-cell">Last login</th>
                <th className="px-4 py-3 text-right font-medium text-slate-600 dark:text-slate-400">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
              {users.map(u => (
                <tr key={u.id} onClick={() => openUser(u.id)} className="cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/30">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand-500/20 text-sm font-bold text-brand-700 dark:text-brand-300">{(u.name || u.email)[0].toUpperCase()}</div>
                      <div className="min-w-0">
                        <p className="truncate font-medium text-slate-900 dark:text-slate-100">{u.name || '(no name)'}</p>
                        <p className="truncate text-xs text-slate-500">{u.email}</p>
                      </div>
                      {u.is_owner && <span className="rounded-md bg-amber-500/10 px-1.5 py-0.5 text-[10px] font-medium text-amber-700 dark:text-amber-300">OWNER</span>}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {u.is_active ? <span className="flex items-center gap-1 text-xs font-medium text-green-600 dark:text-green-400"><CheckCircle size={12} />Active</span>
                      : <span className="flex items-center gap-1 text-xs font-medium text-red-600 dark:text-red-400"><XCircle size={12} />Inactive</span>}
                  </td>
                  <td className="px-4 py-3 text-xs capitalize text-slate-500">{u.role || '—'}</td>
                  <td className="hidden px-4 py-3 text-xs text-slate-500 md:table-cell">{u.last_login_at ? timeAgoWithDate(u.last_login_at) : 'Never'}</td>
                  <td className="px-4 py-3 text-right">
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={!hasUsableEmail(u.email) || u.is_owner || sendingLinkId === u.id}
                      title={u.is_owner ? 'Owner account — use your normal login' : hasUsableEmail(u.email) ? `Email a one-time support link to ${u.email}` : 'No deliverable email on file — add a real email first'}
                      onClick={(e) => { e.stopPropagation(); void handleSendSupportLink(u.id, u.email); }}
                    >
                      {sendingLinkId === u.id ? <RefreshCw size={14} className="animate-spin" /> : <Send size={14} />}
                      Send support link
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {total > perPage && (
        <div className="flex items-center justify-between">
          <span className="text-xs text-slate-500">{((page - 1) * perPage) + 1}–{Math.min(page * perPage, total)} of {total}</span>
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} size="sm"><ChevronLeft size={14} /></Button>
            <Button variant="secondary" onClick={() => setPage(p => p + 1)} disabled={page * perPage >= total} size="sm"><ChevronRight size={14} /></Button>
          </div>
        </div>
      )}

      <Modal open={showModal} onClose={() => setShowModal(false)} title={selectedUser?.email || 'User'}>
        {selectedUser && (
          <div className="space-y-4">
            <div className="flex gap-1 rounded-lg bg-slate-100 p-1 dark:bg-slate-800">
              {([['profile', 'Profile', Users], ['workspaces', 'Workspaces', Building2], ['security', 'Security', Shield]] as const).map(([key, lab, Icon]) => (
                <button key={key} onClick={() => setDetailTab(key)} className={`flex flex-1 items-center justify-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium ${detailTab === key ? 'bg-white text-brand-700 shadow-sm dark:bg-slate-700 dark:text-brand-300' : 'text-slate-600 dark:text-slate-400'}`}>
                  <Icon size={14} />{lab}
                </button>
              ))}
            </div>

            {detailTab === 'profile' && (
              <div className="space-y-3">
                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">Name</label>
                  <div className="flex gap-2">
                    <input type="text" value={editName} onChange={e => setEditName(e.target.value)} className="flex-1 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100" />
                    <Button onClick={handleSaveName} disabled={saving || editName === (selectedUser.name || '')} size="sm">Save</Button>
                  </div>
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">Email</label>
                  <div className="flex gap-2">
                    <input type="email" value={editEmail} onChange={e => setEditEmail(e.target.value)} className="flex-1 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100" />
                    <Button onClick={handleSaveEmail} disabled={saving || editEmail === selectedUser.email} size="sm">Save</Button>
                  </div>
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">Reset password</label>
                  <div className="flex gap-2">
                    <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="New password" className="flex-1 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100" />
                    <Button onClick={handleResetPassword} disabled={saving || !newPassword} size="sm"><KeyRound size={14} />Reset</Button>
                  </div>
                </div>
                <div className="flex items-center justify-between rounded-lg border border-slate-200 p-3 dark:border-slate-700">
                  <div>
                    <p className="text-sm font-medium text-slate-800 dark:text-slate-200">Account status</p>
                    <p className="text-xs text-slate-500">{selectedUser.is_active ? 'Currently active' : 'Currently deactivated'}</p>
                  </div>
                  <Button variant={selectedUser.is_active ? 'secondary' : 'primary'} onClick={handleToggleStatus} disabled={saving || selectedUser.is_owner} size="sm">
                    {selectedUser.is_active ? 'Deactivate' : 'Activate'}
                  </Button>
                </div>
                {!selectedUser.is_owner && (
                  <div className="flex justify-end">
                    <Button variant="secondary" onClick={handleDelete} disabled={saving} size="sm"><Trash2 size={14} />Deactivate account</Button>
                  </div>
                )}
              </div>
            )}

            {detailTab === 'workspaces' && (
              <div className="space-y-2">
                {selectedUser.workspaces.length === 0 ? <p className="text-sm text-slate-500">No workspace memberships.</p> : selectedUser.workspaces.map(ws => (
                  <div key={ws.id} className="flex items-center justify-between rounded-lg border border-slate-200 p-3 dark:border-slate-700">
                    <div>
                      <p className="text-sm font-medium text-slate-800 dark:text-slate-200">{ws.name}</p>
                      <p className="text-xs text-slate-500">{ws.is_creator ? 'Creator' : 'Member'}</p>
                    </div>
                    {!ws.is_creator && (
                      <select value={ws.role} onChange={e => handleRoleChange(ws.id, e.target.value)} className="rounded-lg border border-slate-300 bg-white px-2 py-1 text-xs dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100">
                        <option value="admin">Admin</option>
                        <option value="member">Member</option>
                      </select>
                    )}
                  </div>
                ))}
              </div>
            )}

            {detailTab === 'security' && (
              <div className="space-y-3 text-sm">
                <div className="flex items-center justify-between rounded-lg border border-slate-200 p-3 dark:border-slate-700">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-800 dark:text-slate-200">Support access</p>
                    <p className="text-xs text-slate-500">Email a one-time link that forces a password reset.</p>
                  </div>
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={!hasUsableEmail(selectedUser.email) || selectedUser.is_owner || sendingLinkId === selectedUser.id}
                    title={hasUsableEmail(selectedUser.email) ? `Email a one-time support link to ${selectedUser.email}` : 'No deliverable email on file — add a real email first'}
                    onClick={() => void handleSendSupportLink(selectedUser.id, selectedUser.email)}
                  >
                    {sendingLinkId === selectedUser.id ? <RefreshCw size={14} className="animate-spin" /> : <Send size={14} />}
                    Send support link
                  </Button>
                </div>
                {[['User ID', selectedUser.id], ['Created', selectedUser.created_at ? formatDate(selectedUser.created_at) : '—'],
                  ['Last login', selectedUser.last_login_at ? timeAgoWithDate(selectedUser.last_login_at) : 'Never'],
                  ['Role', selectedUser.is_owner ? 'Platform Owner' : 'Standard User']].map(([k, v]) => (
                  <div key={k} className="rounded-lg border border-slate-200 p-3 dark:border-slate-700">
                    <p className="text-xs font-medium text-slate-500">{k}</p>
                    <p className="mt-1 break-all font-mono text-xs text-slate-700 dark:text-slate-300">{v}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </Modal>

      <Modal open={showCreate} onClose={() => setShowCreate(false)} title="Create new user">
        <div className="space-y-3">
          <div><label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">Name</label>
            <input type="text" value={createName} onChange={e => setCreateName(e.target.value)} placeholder="John Doe" className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100" /></div>
          <div><label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">Email</label>
            <input type="email" value={createEmail} onChange={e => setCreateEmail(e.target.value)} placeholder="user@example.com" className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100" /></div>
          <div><label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">Password</label>
            <input type="password" value={createPassword} onChange={e => setCreatePassword(e.target.value)} placeholder="Minimum 6 characters" className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100" /></div>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={!createEmail || !createName || !createPassword || saving}>{saving ? 'Creating…' : 'Create User'}</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
